import { Link } from "react-router-dom";
import { Phone, Mail, ShieldCheck, Clock, MapPin, BadgeCheck, ArrowRight } from "lucide-react";
import { Seo, faqJsonLd } from "../components/Seo";
import { ServiceIcon } from "../components/ServiceIcon";
import { Faq } from "../components/Faq";
import { CallCta } from "../components/CallCta";
import { Testimonials } from "../components/Testimonials";
import { BUSINESS, SERVICES, CITIES, HOME_FAQS, ORG_JSONLD } from "../data/site";

const HERO_IMG = "https://images.unsplash.com/photo-1566245024852-04fbf7842ce9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwzfHxzZWN1cml0eSUyMGd1YXJkJTIwc3RhbmRpbmclMjBwcm9mZXNzaW9uYWwlMjB1bmlmb3JtfGVufDB8fHx8MTc5MjM3NDI3Nnww&ixlib=rb-4.1.0&q=85";
const PATROL_IMG = "https://images.unsplash.com/photo-1485230405346-71acb9518d9c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxzZWN1cml0eSUyMHBhdHJvbCUyMG5pZ2h0JTIwYnVpbGRpbmclMjBzdXJ2ZWlsbGFuY2V8ZW58MHx8fHwxNzkyMzc0Mjc2fDA&ixlib=rb-4.1.0&q=85";

const WHY = [
  { icon: BadgeCheck, title: "Indigenous-Owned & Operated", text: "A proudly Indigenous security company creating local employment and delivering culturally grounded protection across Manitoba." },
  { icon: Clock, title: "24/7 Dispatch & Response", text: "Our line never closes. Guards, patrols, and alarm response are available day and night, 365 days a year." },
  { icon: MapPin, title: "Province-Wide Coverage", text: "From Winnipeg to Thompson, Brandon to Churchill — one security partner for every Manitoba community." },
  { icon: ShieldCheck, title: "Licensed, Trained Guards", text: "Manitoba-licensed security guards with site-specific training, supervision, and GPS-verified digital reporting." },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      <Seo
        title="Security Guard Services in Manitoba | Winnipeg, Brandon, Thompson | 1st Nations Security"
        description="Security guards, mobile patrols, fire watch and event security across Manitoba. Ask about Winnipeg, Brandon, Steinbach and Thompson."
        keywords="security guards Winnipeg, hospital security Winnipeg, fire watch Manitoba, security company Brandon"
        path="/"
        jsonLd={[ORG_JSONLD, faqJsonLd(HOME_FAQS)]}
      />

      <div className="bg-white" data-testid="ccib-member-band">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-center">
          <img
            src="/ccib-member.png"
            alt="A proud member of the Canadian Council for Indigenous Business — CCIB Certified Indigenous Business"
            className="h-16 sm:h-20 lg:h-24 w-auto object-contain"
          />
        </div>
      </div>

      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Professional uniformed security guard on duty in Manitoba" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-[#0D0E11]/60" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 border border-[#FF9100]/40 bg-[#FF9100]/10 rounded-full px-4 py-1.5 mb-6" data-testid="hero-indigenous-badge">
              <span className="w-2 h-2 rounded-full bg-[#FF9100]" />
              <span className="text-xs font-semibold uppercase tracking-widest text-[#FF9100]">100% Indigenous-Owned & Operated</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-white mb-6">
              Security guard services across <span className="text-[#FF9100]">Manitoba</span>
            </h1>
            <p className="text-lg text-gray-300 leading-relaxed mb-8 max-w-2xl">
              1st Nations Security protects businesses, construction sites, events, and communities in Winnipeg, Brandon, Steinbach, Thompson, and every corner of Manitoba — with licensed guards, mobile patrols, and 24/7 rapid response.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href={BUSINESS.phoneHref} data-testid="hero-call-btn" className="w-full sm:w-auto whitespace-nowrap inline-flex items-center justify-center gap-2 bg-[#FF9100] hover:bg-[#FF5500] text-[#0D0E11] font-bold px-8 py-4 rounded-full transition-all duration-200 hover:scale-105 active:scale-95">
                <Phone className="w-5 h-5" /> Call {BUSINESS.phone}
              </a>
              <a href={BUSINESS.emailHref} data-testid="hero-email-btn" className="w-full sm:w-auto whitespace-nowrap inline-flex items-center justify-center gap-2 border border-white/20 hover:border-[#FF9100] hover:text-[#FF9100] text-white font-semibold px-8 py-4 rounded-full transition-all duration-200">
                <Mail className="w-5 h-5" /> Email Our Team
              </a>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg" data-testid="hero-stats">
              <div><p className="font-heading text-3xl font-bold text-[#FF9100]">24/7</p><p className="text-xs text-gray-400 uppercase tracking-wider mt-1">Dispatch</p></div>
              <div><p className="font-heading text-3xl font-bold text-[#FF9100]">25+</p><p className="text-xs text-gray-400 uppercase tracking-wider mt-1">MB Cities</p></div>
              <div><p className="font-heading text-3xl font-bold text-[#FF9100]">12</p><p className="text-xs text-gray-400 uppercase tracking-wider mt-1">Services</p></div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-semibold uppercase tracking-widest text-[#FF9100] mb-3">What We Do</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white mb-4">Security Services in Manitoba</h2>
          <p className="text-gray-400 max-w-3xl mb-12 leading-relaxed">
            From uniformed security guards and mobile patrol security to fire watch services and event security, every service is delivered by trained, Manitoba-licensed professionals and backed by our 24/7 dispatch.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s) => (
              <Link
                key={s.slug}
                to={`/security-services/${s.slug}`}
                data-testid={`home-service-card-${s.slug}`}
                className="group bg-[#14161C] hover:bg-[#1C1F28] border border-white/8 hover:border-[#FF9100]/40 rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-12 h-12 rounded-xl bg-[#FF9100]/10 border border-[#FF9100]/25 flex items-center justify-center text-[#FF9100] mb-4 group-hover:scale-110 transition-transform duration-300">
                  <ServiceIcon name={s.icon} />
                </div>
                <h3 className="font-heading text-lg font-semibold text-white mb-2 group-hover:text-[#FF9100] transition-colors">{s.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed mb-4">{s.shortDesc}</p>
                <span className="inline-flex items-center gap-1 text-sm font-medium text-[#FF9100]">
                  Learn more <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#0A0B0E]" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-[#FF9100] mb-3">Why 1st Nations Security</p>
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white mb-6">
                Manitoba's Indigenous-Owned Security Guard Company
              </h2>
              <p className="text-gray-400 leading-relaxed mb-8">
                We combine professional, licensed security operations with the values of the communities we come from — respect, vigilance, and commitment. When you hire 1st Nations Security, you get a partner invested in Manitoba's safety and in creating real opportunity for Indigenous people in the security industry.
              </p>
              <div className="space-y-5">
                {WHY.map((w, i) => (
                  <div key={i} className="flex gap-4" data-testid={`why-item-${i}`}>
                    <div className="shrink-0 w-10 h-10 rounded-lg bg-[#FF9100]/10 border border-[#FF9100]/25 flex items-center justify-center text-[#FF9100]">
                      <w.icon className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">{w.title}</h3>
                      <p className="text-sm text-gray-400 leading-relaxed">{w.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img src={PATROL_IMG} alt="Security patrol officer inspecting a building at night in Manitoba" className="rounded-2xl border border-white/10 w-full object-cover aspect-[4/3]" />
              <div className="absolute -bottom-5 -left-5 bg-[#14161C] border border-[#FF9100]/30 rounded-xl px-5 py-4 shadow-2xl">
                <p className="text-xs uppercase tracking-widest text-[#FF9100] font-semibold mb-1">Rapid Response</p>
                <p className="text-sm text-gray-300">Alarm response & mobile patrols province-wide</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20" data-testid="areas-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-semibold uppercase tracking-widest text-[#FF9100] mb-3">Where We Work</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white mb-4">Security Guard Services in Every Manitoba City</h2>
          <p className="text-gray-400 max-w-3xl mb-10 leading-relaxed">
            We provide security guard services in Winnipeg, Brandon, Steinbach, Thompson, and communities across the Capital Region, Westman, Eastman, Interlake, Parkland, Pembina Valley, Central Plains, and Northern Manitoba.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {CITIES.map((c) => (
              <Link
                key={c.slug}
                to={`/security-guards/${c.slug}`}
                data-testid={`home-city-card-${c.slug}`}
                className="group bg-[#14161C] hover:bg-[#1C1F28] border border-white/8 hover:border-[#FF9100]/40 rounded-xl px-4 py-4 transition-all duration-300 hover:-translate-y-0.5"
              >
                <p className="font-semibold text-white text-sm group-hover:text-[#FF9100] transition-colors">{c.name}</p>
                <p className="text-xs text-gray-500 mt-1">{c.region}</p>
              </Link>
            ))}
          </div>
          <div className="mt-8">
            <Link to="/service-areas" data-testid="home-areas-all-link" className="inline-flex items-center gap-2 text-[#FF9100] font-semibold hover:gap-3 transition-all">
              View all Manitoba service areas <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#0A0B0E]" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-white mb-8 text-center">
            Security Services Manitoba — Frequently Asked Questions
          </h2>
          <Faq items={HOME_FAQS} testidPrefix="home-faq" />
        </div>
      </section>

      <Testimonials />
      <CallCta />
    </div>
  );
}
